import HttpClient from './utils/HttpClient';
import APIError from '../errors/APIError';

import { IProduct } from '../models';

class ProductsService {
  private httpClient = HttpClient;

  public async showProduct(
    companyId: number,
    productId: number,
    withStock?: boolean,
  ): Promise<any> {
    try {
      let url = `/companies/${companyId}/products/${productId}`;

      if (withStock) {
        url += `?withStock=${withStock}`;
      }

      const response = await this.httpClient.get(url);

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar obter os dados do produto';
      }

      throw new APIError(errorMessage);
    }
  }

  public async listProducts(
    companyId: number,
    groupId?: number,
  ): Promise<IProduct[]> {
    try {
      let url = `/companies/${companyId}/products`;

      if (groupId) {
        url += `?groupId=${groupId}`;
      }

      const response = await this.httpClient.get(url);

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar listar os produtos';
      }

      throw new APIError(errorMessage);
    }
  }

  public async listRawMaterials(companyId: number): Promise<IProduct[]> {
    try {
      const response = await this.httpClient.get(
        `/companies/${companyId}/products?type=2`,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar listar as matérias primas';
      }

      throw new APIError(errorMessage);
    }
  }

  public async sortProducts(
    companyId: number,
    groupId: number,
    products: Array<Pick<IProduct, 'id' | 'order'>>,
  ): Promise<IProduct[]> {
    try {
      const response = await this.httpClient.patch(
        `/companies/${companyId}/groups/${groupId}/products/sort`,
        {
          products,
        },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar reordenar os produtos';
      }

      throw new APIError(errorMessage);
    }
  }

  public async createProduct(
    companyId: number,
    groupId: number,
    data: FormData,
  ): Promise<any> {
    try {
      const response = await this.httpClient.post(
        `/companies/${companyId}/groups/${groupId}/products`,
        data,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar criar o produto';
      }

      throw new APIError(errorMessage);
    }
  }

  public async editProduct(
    productId: number,
    companyId: number,
    groupId: number,
    data: FormData,
  ): Promise<any> {
    try {
      const response = await this.httpClient.put(
        `/companies/${companyId}/groups/${groupId}/products/${productId}`,
        data,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar editar o produto';
      }

      throw new APIError(errorMessage);
    }
  }

  public async editProductStatus(
    companyId: number,
    groupId: number,
    productId: number,
    pauseSale: boolean,
  ): Promise<IProduct> {
    try {
      const response = await this.httpClient.patch(
        `/companies/${companyId}/groups/${groupId}/products/${productId}/status`,
        { pauseSale },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = `Erro ao tentar ${
          pauseSale ? 'pausar' : 'ativar'
        } a venda do produto`;
      }

      throw new APIError(errorMessage);
    }
  }

  public async editProductImage(
    companyId: number,
    groupId: number,
    productId: number,
    data: FormData,
  ): Promise<IProduct> {
    try {
      const response = await this.httpClient.patch(
        `/companies/${companyId}/groups/${groupId}/products/${productId}/image`,
        data,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = `Erro ao tentar editar a imagem do produto`;
      }

      throw new APIError(errorMessage);
    }
  }

  public async deleteProduct(
    companyId: number,
    productId: number,
  ): Promise<void> {
    try {
      await this.httpClient.delete(
        `/companies/${companyId}/products/${productId}`,
      );
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar excluir o produto';
      }

      throw new APIError(errorMessage);
    }
  }

  public async validatePDVCode(
    companyId: number,
    data: {
      code: string;
      productId?: number;
      productPizzaId?: number | string;
      productGridId?: number | string;
    },
  ): Promise<void> {
    try {
      await this.httpClient.post(
        `/companies/${companyId}/products/validate-pdv-code`,
        data,
      );
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Código já em uso';
      }

      throw new APIError(errorMessage);
    }
  }

  public async importIfoodProducts(
    companyId: number,
    productsData: string,
  ): Promise<any> {
    try {
      const response = await this.httpClient.post(
        `/companies/${companyId}/products/import`,
        {
          productsData
        },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar importar os produtos';
      }

      throw new APIError(errorMessage);
    }
  }
}

export default new ProductsService();
